import { useEffect } from "react";
import { FiAlertTriangle, FiLogOut, FiX } from "react-icons/fi";

export default function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "danger", // "danger" | "warning" | "brand"
  icon: CustomIcon,
  loading = false,
}) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen && !loading) {
        onClose();
      }
    };
    if (isOpen) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, loading, onClose]);

  if (!isOpen) return null;

  const IconComponent = CustomIcon || (type === "danger" ? FiLogOut : FiAlertTriangle);

  const iconBg =
    type === "danger"
      ? "bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400 ring-8 ring-rose-50 dark:ring-rose-900/10"
      : type === "warning"
      ? "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 ring-8 ring-amber-50 dark:ring-amber-900/10"
      : "bg-[#8A9778]/20 text-[#6B7760] dark:text-[#8A9778] ring-8 ring-[#8A9778]/10";

  const confirmBtnCls =
    type === "danger"
      ? "bg-rose-600 hover:bg-rose-700 text-white shadow-md shadow-rose-600/20"
      : type === "warning"
      ? "bg-amber-600 hover:bg-amber-700 text-white shadow-md shadow-amber-600/20"
      : "bg-[#8A9778] hover:bg-[#788568] text-white shadow-md shadow-[#8A9778]/20";

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs transition-opacity animate-in fade-in duration-200"
        onClick={loading ? undefined : onClose}
      />

      {/* Modal Card (SweetAlert Style) */}
      <div className="relative w-full max-w-md transform overflow-hidden rounded-3xl bg-white p-6 sm:p-7 text-center shadow-2xl transition-all duration-300 animate-in zoom-in-95 dark:bg-[#1a2332] dark:border dark:border-white/10">
        {/* Close icon */}
        {!loading && (
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 rounded-xl p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-white/5 dark:hover:text-slate-300 transition-colors"
          >
            <FiX size={18} />
          </button>
        )}

        {/* Top Icon Badge */}
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl mb-5 transition-transform duration-300">
          <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${iconBg}`}>
            <IconComponent size={26} strokeWidth={2.2} />
          </div>
        </div>

        {/* Title & Description */}
        <h3 className="text-lg font-black text-slate-900 dark:text-white mb-2">
          {title}
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed mb-6">
          {message}
        </p>

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row items-center justify-center gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="w-full sm:w-1/2 h-11 inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white font-bold text-sm text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10 transition-all cursor-pointer disabled:opacity-50"
          >
            {cancelText}
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`w-full sm:w-1/2 h-11 inline-flex items-center justify-center gap-2 rounded-xl font-bold text-sm transition-all cursor-pointer disabled:opacity-50 ${confirmBtnCls}`}
          >
            {loading ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            ) : (
              <span>{confirmText}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
